import {
  type ClassificationLevel,
  type ClassificationUsageResponse,
  loosenedEgressLevels,
  schemeChanges,
} from "@esi/shared";

/**
 * The "Before you save" list (openspec change classification-editor, design D5): one item per
 * change of `after` against `before`, in readers' terms, with the file counts where they matter.
 * Keys are under `adminSettings.levels`, except #262's loosening sentences
 * (`adminSettings.egress.*`), kept word for word. A value `{ key }` is translated too.
 */

export type Usage = ClassificationUsageResponse["levels"] | null;

export type Value = string | number | { key: string };

export interface ReviewSentence {
  key: string;
  values?: Record<string, Value>;
}

export interface ReviewItem {
  sentences: ReviewSentence[];
  /** Loosens something, or moves or tightens a level files use. */
  warning: boolean;
}

export interface Review {
  items: ReviewItem[];
  /** A `named` level's outbound treatment loosens: Save is the destructive "Loosen and save". */
  loosensNamed: boolean;
  warning: boolean;
}

/** Joins names: "A and B", "A or B" in the reader's language. */
export interface ListFormat {
  and: (items: readonly string[]) => string;
  or: (items: readonly string[]) => string;
}

const filesOf = (usage: Usage | undefined, id: string) =>
  (usage && Object.hasOwn(usage, id) ? usage[id]?.documents : undefined) ?? 0;

/**
 * The `named` levels of `after` whose egress floor loosens against `before` (#262), with the
 * audience outbound tools now treat them as.
 */
export function namedLoosening(
  before: readonly ClassificationLevel[],
  after: readonly ClassificationLevel[],
): { id: string; label: string; floor: "project" | "entity" }[] {
  const ids = new Set(loosenedEgressLevels(before, after, { named: true }));
  return after.flatMap((level) => {
    // A readers floor lowered from `named` loosens egress too.
    const floor = level.egressFloor ?? level.readersFloor;
    return ids.has(level.id) && (floor === "project" || floor === "entity")
      ? [{ id: level.id, label: level.label, floor }]
      : [];
  });
}

/**
 * `usage` null: an older API (no counts); undefined: not known yet or failed, so a move or a
 * tightening is worded without counts but in the warning tone (it may touch files).
 */
export function schemeReview(
  before: readonly ClassificationLevel[],
  after: readonly ClassificationLevel[],
  usage: Usage | undefined,
  list: ListFormat,
): Review {
  const changes = schemeChanges(before, after);
  const items: ReviewItem[] = [];
  const item = (warning: boolean, ...sentences: ReviewSentence[]) =>
    items.push({ sentences, warning });
  const position = new Map(after.map((l, i) => [l.id, i]));

  for (const level of changes.added) {
    item(false, {
      key: "review.added",
      values: {
        level: level.label,
        position: (position.get(level.id) ?? 0) + 1,
        total: after.length,
      },
    });
  }
  for (const level of changes.renamed) {
    const files = filesOf(usage, level.id);
    item(
      false,
      { key: "review.renamed", values: { from: level.from, to: level.label } },
      ...(files > 0 ? [{ key: "review.renamedFiles", values: { count: files } }] : []),
    );
  }
  // Moves, per level that now sits above levels it was below (a swap reads once).
  const previous = new Map(before.map((l, i) => [l.id, i]));
  const kept = after.filter((l) => previous.has(l.id));
  for (const [i, a] of kept.entries()) {
    const crossed = kept
      .slice(0, i)
      .filter((b) => (previous.get(b.id) ?? 0) > (previous.get(a.id) ?? 0));
    if (crossed.length === 0) {
      continue;
    }
    const below = list.and(crossed.map((b) => b.label));
    const files = [a, ...crossed].reduce((sum, l) => sum + filesOf(usage, l.id), 0);
    item(
      files > 0 || usage === undefined,
      { key: "review.moved", values: { level: a.label, below } },
      ...(files > 0
        ? [
            {
              key: "review.movedFiles",
              values: { count: files, level: a.label, lower: list.or(crossed.map((b) => b.label)) },
            },
          ]
        : []),
    );
  }
  for (const level of changes.readersTightened) {
    const files = filesOf(usage, level.id);
    item(
      files > 0 || usage === undefined,
      { key: `review.readersTightened.${level.to}`, values: { level: level.label } },
      ...(files > 0 ? [{ key: "review.keepReaders", values: { count: files } }] : []),
    );
  }
  for (const level of changes.readersLoosened) {
    item(true, { key: `review.readersLoosened.${level.to}`, values: { level: level.label } });
  }
  for (const level of changes.downloadChanged) {
    item(level.allowed, {
      key: level.allowed ? "review.download.on" : "review.download.off",
      values: { level: level.label },
    });
  }
  const named = namedLoosening(before, after);
  const namedIds = new Set(named.map((l) => l.id));
  for (const level of named) {
    item(true, {
      key: "adminSettings.egress.confirmLevel",
      values: {
        level: level.label,
        audience: { key: `adminSettings.egress.audience.${level.floor}` },
      },
    });
  }
  for (const level of changes.egressLoosened.filter((l) => !namedIds.has(l.id))) {
    item(true, { key: `review.egress.${level.to}`, values: { level: level.label } });
  }
  for (const level of changes.egressTightened) {
    item(false, { key: `review.egress.${level.to}`, values: { level: level.label } });
  }
  if (named.length > 0) {
    item(true, { key: "adminSettings.egress.confirmRisk" });
  }
  for (const level of changes.removed) {
    item(false, { key: "review.removed", values: { level: level.label } });
  }
  return {
    items,
    loosensNamed: named.length > 0,
    warning: items.some((i) => i.warning),
  };
}
